import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import authService from '../../services/authService';
import FormField from '../common/FormField';
import Alert from '../common/Alert';
import './MfaVerify.css';

const MfaVerify = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const { user, verifyMfa, logout } = useAuth();
  const navigate = useNavigate();
  const byEmail = user?.mfa_method === 'email';

  const sendEmailCode = async () => {
    setSendingCode(true);
    setError('');
    setNotice('');
    try {
      await authService.requestMfaEmailCode();
      setCodeSent(true);
      setNotice('A verification code has been emailed to you.');
    } catch (err) {
      setError(err.response?.data?.message || 'The code could not be sent. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await verifyMfa(code, byEmail ? { channel: 'email' } : undefined);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="mfa-verify">
      <h2 className="mfa-verify__title">Two-Factor Authentication</h2>
      <p className="mfa-verify__desc">
        {byEmail
          ? 'We can email you a 6-digit code to finish signing in.'
          : 'Please enter the 6-digit code from your authenticator app.'}
      </p>

      {error && <Alert type="error" message={error} />}
      {notice && <Alert type="success" message={notice} />}

      {byEmail && !codeSent ? (
        <div className="mfa-verify__actions">
          <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
          <button type="button" className="btn-verify" onClick={sendEmailCode} disabled={sendingCode}>
            {sendingCode ? 'Sending…' : 'Email me a code'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleVerify} className="mfa-verify__form">
          <FormField
            label="Verification Code"
            name="code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            required
            autoComplete="one-time-code"
          />

          <div className="mfa-verify__actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
            {byEmail && (
              <button type="button" className="btn-cancel" onClick={sendEmailCode} disabled={sendingCode}>
                {sendingCode ? 'Sending…' : 'Resend code'}
              </button>
            )}
            <button type="submit" className="btn-verify" disabled={loading || code.length !== 6}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MfaVerify;
